import axios, { type AxiosError, type AxiosInstance, type AxiosRequestHeaders } from 'axios'
import axiosAuthRefresh from 'axios-auth-refresh'

type AuthSessionOutput = {
    access_token?: string
    expires_at?: string
    message?: string
}

export type AuthSessionExpiredEventDetail = {
    loginPath: string
    storageKey: string
    refreshPath: string
}

type ConfigureAuthRefreshOptions = AuthSessionExpiredEventDetail

export const AUTH_SESSION_EXPIRED_EVENT = 'cutit:auth-session-expired'
export const BARBER_AUTH_STORAGE_KEY = '@cutit/barber/auth'
export const ADMIN_AUTH_STORAGE_KEY = '@cutit/admin/auth'
export const PLATFORM_ADMIN_AUTH_STORAGE_KEY = '@cutit/platform-admin/auth'

const resolveCreateAuthRefresh = (moduleExport: unknown) => {
    if (typeof moduleExport === 'function') {
        return moduleExport as typeof axiosAuthRefresh
    }

    const candidates = moduleExport as {
        createAuthRefresh?: unknown
        default?: unknown
    }

    if (typeof candidates.createAuthRefresh === 'function') {
        return candidates.createAuthRefresh as typeof axiosAuthRefresh
    }

    if (typeof candidates.default === 'function') {
        return candidates.default as typeof axiosAuthRefresh
    }

    throw new TypeError('axios-auth-refresh export is not callable')
}

const createAuthRefresh = resolveCreateAuthRefresh(axiosAuthRefresh)

const refreshClient = axios.create({
    baseURL: import.meta.env?.VITE_API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
})

const authRoutePattern = /^\/api\/auth\/(?:admin|barber)\/(?:login|logout)$/

const getDefaultHeaders = (instance: AxiosInstance) => {
    return instance.defaults.headers as Record<string, string | undefined>
}

const getRequestHeaders = (error: AxiosError) => {
    return error.config?.headers as AxiosRequestHeaders | undefined
}

const setRequestAuthorizationHeader = (
    headers: AxiosRequestHeaders | undefined,
    token?: string
) => {
    if (!headers) {
        return
    }

    const mutableHeaders = headers as AxiosRequestHeaders & {
        delete?: (headerName: string) => boolean
        set?: (headerName: string, value: string) => void
    }

    if (token) {
        if (typeof mutableHeaders.set === 'function') {
            mutableHeaders.set('Authorization', `Bearer ${token}`)

            return
        }

        mutableHeaders.Authorization = `Bearer ${token}`

        return
    }

    if (typeof mutableHeaders.delete === 'function') {
        mutableHeaders.delete('Authorization')

        return
    }

    delete mutableHeaders.Authorization
}

const isBrowser = () => globalThis.window !== undefined && globalThis.localStorage !== undefined

export const readStoredAccessToken = (storageKey: string) => {
    if (!isBrowser()) {
        return undefined
    }

    const storedValue = globalThis.localStorage.getItem(storageKey)

    if (!storedValue) {
        return undefined
    }

    try {
        const parsedValue = JSON.parse(storedValue) as unknown

        return typeof parsedValue === 'string' ? parsedValue : undefined
    } catch {
        return storedValue
    }
}

export const setStoredAccessToken = (storageKey: string, token: string) => {
    if (!isBrowser()) {
        return
    }

    globalThis.localStorage.setItem(storageKey, JSON.stringify(token))
}

export const setAuthorizationHeader = (instance: AxiosInstance, token?: string) => {
    const headers = getDefaultHeaders(instance)

    if (token) {
        headers.Authorization = `Bearer ${token}`

        return
    }

    delete headers.Authorization
}

export const clearAuthSession = (instance: AxiosInstance, storageKey: string) => {
    setAuthorizationHeader(instance)

    if (isBrowser()) {
        globalThis.localStorage.removeItem(storageKey)
    }
}

const notifyAuthSessionExpired = (detail: AuthSessionExpiredEventDetail) => {
    if (!isBrowser()) {
        return
    }

    globalThis.dispatchEvent(
        new CustomEvent<AuthSessionExpiredEventDetail>(AUTH_SESSION_EXPIRED_EVENT, {
            detail,
        })
    )
}

const shouldRefreshRequest = (storageKey: string, error: AxiosError) => {
    if (error.response?.status !== 401) {
        return false
    }

    const requestUrl = error.config?.url ?? ''

    if (authRoutePattern.test(requestUrl)) {
        return false
    }

    return Boolean(readStoredAccessToken(storageKey) || getRequestHeaders(error)?.Authorization)
}

export const configureAuthRefresh = (
    instance: AxiosInstance,
    { loginPath, storageKey, refreshPath }: ConfigureAuthRefreshOptions
) => {
    setAuthorizationHeader(instance, readStoredAccessToken(storageKey))

    createAuthRefresh(
        instance,
        async (failedRequest) => {
            try {
                const { data } = await refreshClient.post<AuthSessionOutput>(refreshPath)
                const accessToken = data.access_token

                if (!accessToken) {
                    throw new Error('Refresh response is missing access_token')
                }

                setStoredAccessToken(storageKey, accessToken)
                setAuthorizationHeader(instance, accessToken)

                if (failedRequest.response?.config?.headers) {
                    setRequestAuthorizationHeader(
                        failedRequest.response.config.headers,
                        accessToken
                    )
                }
            } catch (error) {
                clearAuthSession(instance, storageKey)
                notifyAuthSessionExpired({ loginPath, storageKey, refreshPath })

                throw error
            }
        },
        {
            interceptNetworkError: false,
            onRetry: (requestConfig) => {
                setRequestAuthorizationHeader(
                    requestConfig.headers,
                    readStoredAccessToken(storageKey)
                )

                return requestConfig
            },
            shouldRefresh: (error) => shouldRefreshRequest(storageKey, error),
            statusCodes: [401],
        }
    )
}
